import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import Button from '@atlaskit/button/new';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos, findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import CoreCloseIcon from '@atlaskit/icon/core/close';
import CoreMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import CoreThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import CoreThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { LogoSvg } from '@atlassian/generative-ai-modal/assets/Logo';
import { highlightSentence, unhighlightSentence } from '../pm-plugins/ai-spelling-grammar/commands';
import { insertSuggestionWithAnalytics, removeSuggestionWithAnalytics } from '../pm-plugins/ai-spelling-grammar/commands-with-analytics';
import { getPluginState as getSpellingAndGrammarPluginState } from '../pm-plugins/ai-spelling-grammar/plugin-factory';
import { getBlockOrSentenceFromDiffObject, getSelectedDiffObject } from '../pm-plugins/ai-spelling-grammar/utils';
import { createOpenAIModalCommand } from '../pm-plugins/decoration/actions';
import { getPluginState as getDecorationPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import { getAIExperienceButton } from '../pm-plugins/floating-toolbar-button/floating-toolbar-button';
import { improveWriting } from '../prebuilt/config-items-range/enhance/enhance';
import { getSupportedNodesForAIModal } from '../utils/ai-button';
import { AIFloatingToolbarFooter } from './floating-toolbar-footer/AIFloatingToolbarFooter';
import { messages } from './messages';
import { separator } from './seperator';
var jsonTransformer = new JSONTransformer();
var CloseIcon = function CloseIcon(_ref) {
  var label = _ref.label;
  return jsx(CoreCloseIcon, {
    label: label,
    color: "var(--ds-icon, #44546F)",
    spacing: "spacious"
  });
};
var MoreIcon = function MoreIcon(_ref2) {
  var label = _ref2.label;
  return jsx(CoreMoreIcon, {
    label: label,
    spacing: "spacious",
    color: "currentColor"
  });
};
var ImproveSentenceIcon = function ImproveSentenceIcon(_ref3) {
  var label = _ref3.label;
  return jsx(LogoSvg, {
    alt: label
  });
};
export var getFloatingToolbarConfig = function getFloatingToolbarConfig(_ref4) {
  var aiGlobalOptIn = _ref4.aiGlobalOptIn,
    editorPluginAIProvider = _ref4.editorPluginAIProvider;
  return function (state, intl, providerFactory, processedConfigs) {
    var pluginState = getSpellingAndGrammarPluginState(state) || {};
    var isSpellingAndGrammarEnabled = pluginState.isSpellingAndGrammarEnabled,
      selectedID = pluginState.selectedID,
      splitParagraphIntoSentences = pluginState.splitParagraphIntoSentences;
    var product = editorPluginAIProvider.product,
      handleFeedbackSubmission = editorPluginAIProvider.handleFeedbackSubmission;
    if (isSpellingAndGrammarEnabled && state.selection.from === state.selection.to) {
      var decorationPluginState = getDecorationPluginState(state);
      if (decorationPluginState.modalDecorationSet === DecorationSet.empty) {
        var selectedDiff = getSelectedDiffObject(pluginState, state.selection.to);
        if (selectedDiff && selectedID) {
          var blockOrSentenceFromDiffObject = getBlockOrSentenceFromDiffObject(pluginState, selectedDiff);
          var sentenceItems = [];
          if (splitParagraphIntoSentences && blockOrSentenceFromDiffObject) {
            /**
             * If splitParagraphIntoSentences is true,
             * blockOrSentenceFromDiffObject will return a ProactiveAISentence
             * We use blockOrSentenceFromDiffObject to get extra metadata about
             * the sentence for analytics
             */
            var sentence = blockOrSentenceFromDiffObject;
            var openAIModalAndUnhighlightSentenceCommand = function openAIModalAndUnhighlightSentenceCommand(state, dispatch) {
              return createOpenAIModalCommand({
                state: state,
                configItem: improveWriting,
                aiGlobalOptIn: aiGlobalOptIn,
                positions: {
                  startPos: sentence.from,
                  endPos: sentence.to
                },
                lastTriggeredFrom: 'proactiveAIFloatingToolbar'
              })(state, dispatch) &&
              // If modal opens, remove the hover sentence highlight
              unhighlightSentence()(state, dispatch);
            };
            sentenceItems.push(separator(), {
              id: 'editor.ai.sg-improve-sentence',
              type: 'button',
              testId: 'ak-editor-ai-suggestion-toolbar-improve-sentence-button',
              icon: ImproveSentenceIcon,
              onClick: openAIModalAndUnhighlightSentenceCommand,
              onMouseEnter: highlightSentence(sentence),
              onMouseLeave: unhighlightSentence(),
              onFocus: highlightSentence(sentence),
              onBlur: unhighlightSentence(),
              title: intl.formatMessage(messages.improveSentenceToolbarButtonLabel),
              showTitle: true
            });
          }
          var dropdownOptions = [{
            title: intl.formatMessage(messages.correctSuggestion),
            icon: jsx(CoreThumbsUpIcon, {
              label: "thumbsupIcon",
              spacing: "spacious",
              color: "var(--ds-icon, #44546F)"
            }),
            onClick: function onClick() {
              if (!handleFeedbackSubmission || !blockOrSentenceFromDiffObject) {
                return false;
              }
              handleFeedbackSubmission({
                sentiment: 'good-suggestion',
                getAIExperience: function getAIExperience(hasUserConsent) {
                  return _objectSpread({}, hasUserConsent ? {
                    originalText: selectedDiff.originalText,
                    suggestionText: selectedDiff.text,
                    selectedSentence: blockOrSentenceFromDiffObject.text
                  } : {});
                },
                editorAttributes: {
                  product: product
                }
              });
              return true;
            }
          }, {
            title: intl.formatMessage(messages.incorrectSuggestion),
            icon: jsx(CoreThumbsDownIcon, {
              label: "thumbsdownIcon",
              spacing: "spacious",
              color: "var(--ds-icon, #44546F)"
            }),
            onClick: function onClick() {
              if (!handleFeedbackSubmission || !blockOrSentenceFromDiffObject) {
                return false;
              }
              handleFeedbackSubmission({
                sentiment: 'bad-suggestion',
                getAIExperience: function getAIExperience(hasUserConsent) {
                  return _objectSpread({}, hasUserConsent ? {
                    originalText: selectedDiff.originalText,
                    suggestionText: selectedDiff.text,
                    selectedSentence: blockOrSentenceFromDiffObject.text
                  } : {});
                },
                editorAttributes: {
                  product: product
                }
              });
              return true;
            }
          }];
          var nodeType = [state.schema.nodes.paragraph, state.schema.nodes.text, state.schema.nodes.heading, state.schema.nodes.taskItem, state.schema.nodes.decisionItem];
          var _from = selectedDiff.from,
            text = selectedDiff.text,
            replacement = selectedDiff.replacement,
            hasInlineNodes = selectedDiff.hasInlineNodes;
          var textToolbarItemButton = {
            id: 'editor.ai.sg-suggestion',
            type: 'button',
            showTitle: true,
            testId: 'ak-editor-ai-suggestion-toolbar-suggestion-button',
            onClick: insertSuggestionWithAnalytics({
              schema: state.schema,
              selectedDiff: selectedDiff,
              blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject,
              aiInteractionID: selectedID
            }),
            title: text,
            focusEditoronEnter: true
          };
          var toolbarItemButton = hasInlineNodes && replacement && fg('platform_editor_ai_proactive_ai_md_plus') ? {
            type: 'custom',
            render: function render(view) {
              if (!view || !replacement) {
                return;
              }
              var nodes = view.state.schema.nodes;
              var doc = nodes.doc,
                paragraph = nodes.paragraph;
              var replacementDoc = doc.createAndFill({}, paragraph.createAndFill({}, replacement));
              if (!replacementDoc) {
                return;
              }
              return jsx(Button, {
                spacing: "compact",
                appearance: "subtle",
                onClick: function onClick() {
                  insertSuggestionWithAnalytics({
                    schema: state.schema,
                    selectedDiff: selectedDiff,
                    blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject,
                    aiInteractionID: selectedID
                  })(view.state, view.dispatch);
                }
              }, jsx(RendererActionsContext, null, jsx(ReactRenderer, {
                document: jsonTransformer.encode(replacementDoc),
                useSpecBasedValidator: true,
                dataProviders: providerFactory
              })));
            },
            fallback: [textToolbarItemButton]
          } : textToolbarItemButton;
          var items = [toolbarItemButton].concat(sentenceItems, [separator(), {
            id: 'editor.ai.sg-dismiss',
            type: 'button',
            testId: 'ak-editor-ai-suggestion-toolbar-dismiss-button',
            icon: CloseIcon,
            onClick: removeSuggestionWithAnalytics({
              schema: state.schema,
              selectedDiff: selectedDiff,
              blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject,
              aiInteractionID: selectedID
            }),
            title: intl.formatMessage(messages.dismissSuggestion),
            focusEditoronEnter: true
          }, separator(), {
            type: 'dropdown',
            title: 'More options',
            id: 'editor.ai.sg-dropdown',
            icon: MoreIcon,
            options: dropdownOptions,
            hideExpandIcon: true,
            alignDropdownWithToolbar: false,
            dropdownWidth: 200,
            // Width of the dropdown at the moment hardcoded to 200px
            footer: jsx(AIFloatingToolbarFooter, null)
          }]);
          return {
            title: 'Spelling & Grammar controls',
            getDomRef: function getDomRef(editorView) {
              return findDomRefAtPos(_from, editorView.domAtPos.bind(editorView));
            },
            nodeType: nodeType,
            zIndex: akEditorFloatingPanelZIndex + 1,
            // Place the context menu slightly above the others
            items: items,
            scrollable: false,
            align: 'left'
          };
        }
      }
    }

    //TODO: AI Button experiment cleanup - platform_editor_ai_ai_button_block_elements
    var selection = state.selection;
    var from = selection.from,
      to = selection.to;
    var isNodeSelection = selection instanceof NodeSelection;
    var isCellSelection = selection.toJSON().type === 'cell';
    var getParentSupportedForAIButton = function getParentSupportedForAIButton() {
      var supportedNodesForAI = getSupportedNodesForAIModal(state);
      if (isNodeSelection) {
        return supportedNodesForAI.has(selection.node.type) ? selection.node : undefined;
      } else {
        var parentNodeFindResult = findParentNode(function (node) {
          return supportedNodesForAI.has(node.type);
        })(selection);
        return parentNodeFindResult === null || parentNodeFindResult === void 0 ? void 0 : parentNodeFindResult.node;
      }
    };
    var parentSupportedForAIButton = getParentSupportedForAIButton();
    if ((from === to || isNodeSelection || isCellSelection) && parentSupportedForAIButton) {
      if (editorExperiment('platform_editor_ai_ai_button_block_elements', 'test', {
        exposure: true
      })) {
        var canAddAIButton = isNodeSelection || parentSupportedForAIButton;
        var _nodeType = parentSupportedForAIButton === null || parentSupportedForAIButton === void 0 ? void 0 : parentSupportedForAIButton.type;
        if (canAddAIButton && _nodeType) {
          var aiIconButton = getAIExperienceButton({
            intl: intl,
            rangeBaseGenerate: editorPluginAIProvider.rangeConfig.baseGenerate,
            hideTitle: true,
            aiGlobalOptIn: aiGlobalOptIn,
            triggeredFor: _nodeType
          });
          var addAIButtonToConfig = function addAIButtonToConfig(title) {
            var toolbarConfig = processedConfigs === null || processedConfigs === void 0 ? void 0 : processedConfigs.find(function (config) {
              return config.title === title;
            });
            var items = Array.isArray(toolbarConfig === null || toolbarConfig === void 0 ? void 0 : toolbarConfig.items) ? toolbarConfig === null || toolbarConfig === void 0 ? void 0 : toolbarConfig.items : [];
            if (toolbarConfig && items.length) {
              var withAIItems = [].concat(_toConsumableArray(items.slice(0, items.length - 3)), [aiIconButton, {
                type: 'separator'
              }], _toConsumableArray(items.slice(items.length - 3, items.length)));
              /**
               * Updating items of table, panel, layout or expand config item.
               * Injecting updated items in same config object.
               */
              toolbarConfig.items = withAIItems;
              return toolbarConfig;
            }
          };

          /**
           * Here we are updating items of table, panel, expand and layout
           * 	floaingToolbarConfig.
           * This will only work when editor-plugin-ai is added after these plugins.
           * Proper solution will be implemented when outcome
           * 	of https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4166466469/Editor+RFC+058+Easier+way+to+insert+items+in+floating+toolbar
           *  RFC is finalised.
           */
          var toolbarConfig = addAIButtonToConfig('Panel floating controls') || addAIButtonToConfig('Table floating controls') || addAIButtonToConfig('Layout floating controls') || addAIButtonToConfig('Expand toolbar');
          if (toolbarConfig) {
            return toolbarConfig;
          }
        }
      }
    }
    return;
  };
};